"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/components/toast-provider";
import Link from "next/link";
import { authButton, authCard, authInput, authLabel, authPage, authRow } from "@/lib/auth-form";
import { ClearableInput } from "@/components/clearable-input";

export default function LoginPage() {
  const { user, employee, loading, login } = useAuth();
  const { showToast } = useToast();
  const router = useRouter();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // reimbursements.mmalson.com is the employees' door into the same app.
  const [employeeHost, setEmployeeHost] = useState(false);
  useEffect(() => setEmployeeHost(window.location.hostname.startsWith("reimbursements.")), []);

  useEffect(() => {
    if (!loading && user) {
      router.replace(employee ? "/my-payroll" : "/dashboard");
    }
  }, [loading, user, employee, router]);

  if (loading || user) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-100px)]">
        <p className="text-muted">Loading...</p>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const isEmployee = await login(username, password);
      router.push(isEmployee ? "/my-payroll" : "/dashboard");
    } catch (err) {
      showToast("error", err instanceof Error ? err.message : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className={authPage}>
      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-cyan">{employeeHost ? "Reimbursements" : "Sign In"}</h1>
          {employeeHost && (
            <p className="mt-1 text-sm text-muted">Sign in with your work email to enter hours and expenses.</p>
          )}
        </div>
        <div className={authCard}>
          <div className={authRow}>
            <label className={authLabel} htmlFor="login-user">
              {employeeHost ? "Work email" : "Username or email"}
            </label>
            <ClearableInput
              id="login-user"
              type={employeeHost ? "email" : "text"}
              inputMode={employeeHost ? "email" : "text"}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={authInput}
              autoFocus
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
          <div className={authRow}>
            <label className={authLabel} htmlFor="login-password">
              Password
            </label>
            <ClearableInput
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={authInput}
              autoComplete="current-password"
            />
          </div>
        </div>
        <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
          {employeeHost && (
            <Link href="/forgot-password" className="order-2 text-center text-sm text-cyan hover:underline sm:order-1">
              Forgot your password?
            </Link>
          )}
          <button type="submit" disabled={submitting} className={authButton + " order-1 sm:order-2 sm:ml-auto"}>
            {submitting ? "Signing in..." : "Sign In"}
          </button>
        </div>
      </form>
    </div>
  );
}
